import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect } from 'react';

import { PublicApiLog } from '@/models/Log';
import { PublicApiLogResponse } from '@/models/network';
import useBuildDetailsPageStore, {
  ExpiringRawLogUrlStatus,
  updateLogUrlStatus,
} from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import { publicApi } from '@/utils/api';
import { defaultQueryRetryCount } from '@/utils/query';

type UseExpiringRawLogUrlResponse = {
  status: ExpiringRawLogUrlStatus;
  url?: string;
  refetch: () => Promise<QueryObserverResult<string | undefined, AxiosError>>;
};

const useExpiringRawLogUrl = (
  appSlug: string,
  buildSlug: string,
  isFinished: boolean,
  publicAccess: boolean,
): UseExpiringRawLogUrlResponse => {
  const rawUrlStatus = useBuildDetailsPageStore((state) => state.logUrlStatus);
  const canViewBuildLogs = useBuildDetailsPageStore((state) => state.abilities.canViewBuildLogs);
  // The URL gets generated somewhere after the build finished.
  const enabled =
    (canViewBuildLogs || publicAccess) && isFinished && rawUrlStatus !== 'deleted' && rawUrlStatus !== 'not_supported';

  const {
    data: url,
    refetch,
    error,
    isPending,
  } = useQuery<PublicApiLogResponse<PublicApiLog>, AxiosError<Error>, string | undefined>({
    queryKey: ['expiringRawLogUrl', appSlug, buildSlug, publicAccess],

    queryFn: async () => {
      const { data } = await publicApi.get<PublicApiLogResponse<PublicApiLog>>(
        `/v0.1/apps/${appSlug}/builds/${buildSlug}/log`,
        {
          publicAccess,
        },
      );

      return data;
    },
    select(data) {
      return data.expiringRawLogUrl;
    },
    enabled,
    refetchInterval: (query) =>
      query.state.data?.expiringRawLogUrl || (query.state.error as AxiosError | null)?.response?.status === 403
        ? false
        : 5000,
    retry: rawUrlStatus !== 'deleted' ? false : defaultQueryRetryCount,
  });

  let status: ExpiringRawLogUrlStatus = rawUrlStatus;
  if (error?.response?.status === 404) {
    status = 'deleted';
  } else if (error?.response?.status === 422) {
    status = 'not_supported';
  } else if (!url && !isPending) {
    status = 'generating';
  } else if (url && !isPending) {
    status = 'present';
  }

  useEffect(() => {
    if (enabled) {
      updateLogUrlStatus(status);
    }
  }, [status, enabled]);

  if (!url) {
    return {
      status,
      refetch,
    };
  }

  return {
    status,
    url,
    refetch,
  };
};

export default useExpiringRawLogUrl;
